import { Auth } from "@aws-amplify/auth";

class CognitoAuthService {
  // Store impersonation token (staff's token when admin is impersonating)
  _impersonationToken = null;

  /**
   * Set the impersonation token (staff's Cognito ID token)
   * When set, getIdToken() will return this token instead of the admin's token
   * @param {string} token - The staff's Cognito ID token
   */
  setImpersonationToken(token) {
    this._impersonationToken = token;
  }

  /**
   * Clear the impersonation token
   * After clearing, getIdToken() will return the admin's token
   */
  clearImpersonationToken() {
    this._impersonationToken = null;
  }

  /**
   * Check if an impersonation token is currently set
   * @returns {boolean}
   */
  hasImpersonationToken() {
    return !!this._impersonationToken;
  }

  async validateSession() {
    try {
      // Check if session exists and is valid
      const session = await Auth.currentSession();

      if (!session) {
        return { valid: false, error: "no_session" };
      }

      // Get current user info
      const user = await Auth.currentAuthenticatedUser();

      return {
        valid: true,
        user: {
          cognitoId: user.attributes.sub,
          username: user.username,
          email: user.attributes.email,
        },
      };
    } catch (error) {
      console.error("Cognito session validation error:", error);
      return { valid: false, error: error.message };
    }
  }

  /**
   * Get Cognito ID token string for API calls
   * If an impersonation token is set, returns that instead of the admin's token
   * @returns {Promise<string|null>}
   */
  async getIdToken() {
    // If impersonation token is set, return it instead of the admin's token
    if (this._impersonationToken) {
      // Note: Logging disabled to reduce console noise during impersonation
      // (getIdToken is called on every API request via axios interceptor)
      return this._impersonationToken;
    }

    try {
      const session = await Auth.currentSession();
      const idToken = session.getIdToken();
      const jwtToken = idToken.getJwtToken();

      // Ensure we return a string, not an object
      if (typeof jwtToken !== 'string') {
        console.error("getIdToken: Expected string but got:", typeof jwtToken, jwtToken);
        return null;
      }

      return jwtToken;
    } catch (error) {
      console.error("Error getting ID token:", error);
      return null;
    }
  }

  /**
   * Get custom claims from the Cognito ID token
   * Returns the current account context set by Auth Portal
   * @returns {Promise<{customerId: string|null, accountId: string|null, role: string|null, cognitoId: string|null}>}
   */
  async getTokenClaims() {
    try {
      const session = await Auth.currentSession();
      const idToken = session.getIdToken();
      const payload = idToken.payload;

      return {
        cognitoId: payload.sub || null,
        customerId: payload["custom:current_customer_id"] || null,
        accountId: payload["custom:current_account_id"] || null,
        role: payload["custom:current_account_role"] || null,
      };
    } catch (error) {
      console.error("Error getting token claims:", error);
      return {
        cognitoId: null,
        customerId: null,
        accountId: null,
        role: null,
      };
    }
  }
}

export default new CognitoAuthService();
